import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { X, Maximize2, Minimize2, ThumbsUp, ThumbsDown, Copy, WifiOff, RefreshCcw, Mic, MicOff, Mail } from 'lucide-react';

import type { ChatWidgetConfig, ChatMessage } from './types';
import {
  ChatAPI,
  CUSTOM_OPTION_VALUE,
  QUERY_TYPE,
  hasAnswerButtons,
  isTypedQuestion,
  type ContactFormPayload,
  type QualificationAnswerPayload,
  type QualificationOptionsPayload,
} from './api';
import { QualificationOptions } from './components/QualificationOptions';
import { YourTurnChip } from './components/YourTurnChip';
import { ContactFormCard } from './components/ContactFormCard';
import { ChatSummaryCard } from './components/ChatSummaryCard';
import { ChatStorage } from './storage';
import { getCampaignRef, getCurrentPageUrl, getSessionId, stripQuestionFromMessage } from './utils';
import { StreamingMessage } from './components/StreamingMessage';
import { useStreamingText } from './useStreamingText';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import { ExpandedAdvisorView, SHARE_MIN_USER_MESSAGES } from './components/ExpandedAdvisorView';

export interface ChatWidgetController {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
}

interface ChatWidgetProps {
  config: ChatWidgetConfig;
  onRegisterController?: (controller: ChatWidgetController) => void;
}

const FALLBACK_QUESTIONS: string[] = [
  "Can you build a platform like Polymarket?",
  "Can you build a Season Long Fantasy?",
  "How do Fanzone increase user engagement?",
];

export function ChatWidget({ config, onRegisterController }: ChatWidgetProps) {
  const sessionIdRef = useRef<string>(getSessionId());
  const apiRef = useRef<ChatAPI>(new ChatAPI(
    config.apiUrl || 'https://staging-ai-api.vinfotech.com/api',
    config.chatEndpoint || 'sales-bot'
  ));
  const storageRef = useRef<ChatStorage>(
    // 180 minutes of inactivity. The timestamp is rewritten on every save, so
    // this is an idle window rather than a total lifetime — a conversation with
    // messages throughout never expires mid-flow.
    new ChatStorage('vinfotech_chat_', 180, true)
  );

  const cachedStateRef = useRef(storageRef.current.loadChatHistory(sessionIdRef.current));
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [question, setQuestion] = useState('');
  const initialHistory = cachedStateRef.current;
  const initialMessages = initialHistory.messages;
  const defaultFallbackSuggestions = config.suggestions?.fallbackQuestions && config.suggestions.fallbackQuestions.length > 0
    ? config.suggestions.fallbackQuestions
    : FALLBACK_QUESTIONS;

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [initialSuggestions, setInitialSuggestions] = useState<string[]>(defaultFallbackSuggestions);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>(
    initialMessages.length > 0 ? initialHistory.suggestions : []
  );
  const [clickedSuggestions, setClickedSuggestions] = useState<Set<string>>(new Set());
  const [showTooltip, setShowTooltip] = useState(true);
  const [isTooltipClosing, setIsTooltipClosing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showPredefinedQuestions, setShowPredefinedQuestions] = useState(
    initialMessages.length === 0
  );
  const [visibleSuggestionsCount, _setVisibleSuggestionsCount] = useState(3);
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'positive' | 'negative'>>(
    initialHistory.feedback
  );
  const [currentTheme] = useState<'light' | 'dark'>('light');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  // The discovery question the latest reply asked, with its clickable answers.
  // Cleared the moment anything is sent, so a stale row cannot be answered two
  // turns after the question moved on.
  const [qualification, setQualification] = useState<QualificationOptionsPayload | null>(
    initialMessages.length > 0 ? initialHistory.qualification : null
  );
  // A lead question offered as the last suggestion chip ("Your turn"). Like
  // the question above, it belongs to the latest reply and is cleared the
  // moment anything is sent.
  const [yourTurn, setYourTurn] = useState<QualificationOptionsPayload | null>(
    initialMessages.length > 0 ? initialHistory.yourTurn : null
  );
  // Set when they pick "Something else": the next typed message answers this
  // question instead of starting a new one. The composer stays the composer —
  // one text box, always in the same place — and only its placeholder changes.
  const [customFor, setCustomFor] = useState<QualificationOptionsPayload | null>(null);
  const [contactForm, setContactForm] = useState<ContactFormPayload | null>(
    initialMessages.length > 0 ? initialHistory.contactForm : null
  );
  // What they have typed into that form but not yet sent. Held here rather than
  // inside the card so a half-filled form survives a refresh — most visitors
  // will not retype an email they already entered once.
  const [contactValues, setContactValues] = useState<Record<string, string>>(
    cachedStateRef.current.contactValues
  );
  // Whether the "email me a summary" card is open. Not persisted: it is a
  // thing the visitor opens in the moment, and restoring it on a later visit
  // would put a form in front of someone who never asked for one.
  const [summaryOpen, setSummaryOpen] = useState(false);
  // How many questions the visitor has asked. Gates both the header icon and
  // the card below, so clearing the chat cannot leave an open card with no
  // icon left to close it.
  const userMessageCount = messages.filter((m) => m.type === 'user').length;
  const canShareSummary = userMessageCount >= SHARE_MIN_USER_MESSAGES;
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasError, setHasError] = useState(false);
  const [lastQuestion, setLastQuestion] = useState('');
  // How the last message was sent, so Retry resends it as the same kind of
  // message rather than as an unlabelled one.
  const lastSendRef = useRef<{ queryType?: number; answer?: QualificationAnswerPayload }>({});
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const receivedChunksRef = useRef<boolean>(false);
  const firstChunkRef = useRef<boolean>(true);
  const streamingMessageIndexRef = useRef<number>(-1);
  const recognitionRef = useRef<any>(null); // For Speech Recognition
  const isResponseCompleteRef = useRef<boolean>(false);
  const completeResponseReceivedRef = useRef<boolean>(false);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [isActivelyStreaming, setIsActivelyStreaming] = useState(false);

  // Paced reveal, shared with the admin test page (src/lib/utils/useStreamingText.ts).
  // It steers the reveal by how fast text is ARRIVING, averaged over ~1.5s,
  // rather than by how much is queued — a backlog-driven rate makes every
  // chunk reveal as the same fast-then-slow arc, which is what reads as
  // stutter. `fullAnswerRef` below keeps the authoritative text so the turn can
  // be committed in full regardless of where the animation has got to.
  const typewriter = useStreamingText();
  const fullAnswerRef = useRef<string>('');


  const primaryColor = config.theme?.primaryColor || '#6720ff';
  const welcomeMessage = config.messages?.welcomeMessage || 'How can I help you?';
  const placeholderText = config.messages?.placeholderText || 'Ask us..';

  const loadingMessages = [
    'Analyzing your query...',
    'Searching the knowledge base...',
    'Processing information...',
    'Finalizing the answer...'
  ];

  useEffect(() => {
    if (config.behavior?.autoOpen) {
      setIsOpen(true);
    }

    loadInitialSuggestions();

    // Theme listening disabled to force light mode

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

    };
  }, []);

  // Register public SDK controller methods
  useEffect(() => {
    if (onRegisterController) {
      onRegisterController({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen(prev => !prev),
        isOpen: () => isOpen,
      });
    }
  }, [onRegisterController, isOpen]);

  // Support window-level custom events for programmatic control
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);
    const handleToggle = () => setIsOpen(prev => !prev);

    window.addEventListener('vinfotech-widget:open', handleOpen);
    window.addEventListener('vinfotech-widget:close', handleClose);
    window.addEventListener('vinfotech-widget:toggle', handleToggle);

    return () => {
      window.removeEventListener('vinfotech-widget:open', handleOpen);
      window.removeEventListener('vinfotech-widget:close', handleClose);
      window.removeEventListener('vinfotech-widget:toggle', handleToggle);
    };
  }, []);

  // Listen for URL changes without monkey-patching host history or observing entire document
  useEffect(() => {
    let lastUrl = window.location.href;

    const handleUrlChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        
        // Fetch new suggestions based on the new URL
        const currentPageUrl = getCurrentPageUrl();
        apiRef.current.fetchInitialSuggestions(
          true,
          currentPageUrl
        ).then(suggestions => {
          if (suggestions.length > 0) {
            setInitialSuggestions(suggestions);
            
            // Only update dynamic suggestions if we have an active chat stream
            if (messages.length > 0) {
              setDynamicSuggestions(suggestions);
            } else {
              setDynamicSuggestions([]);
            }
          }
        }).catch(err => {
          console.error('Failed to load new suggestions on URL change:', err);
          // Fallback: Keep the last questions (do not clear or revert)
        });
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('vinfotech:urlchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('vinfotech:urlchange', handleUrlChange);
    };
  }, [messages]);

  // Refresh suggestions on open in case the user navigated while the widget was closed
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const currentPageUrl = getCurrentPageUrl();
      apiRef.current.fetchInitialSuggestions(true, currentPageUrl)
        .then(suggestions => {
          if (suggestions.length > 0) {
            setInitialSuggestions(suggestions);
          }
        })
        .catch(() => {});
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (showTooltip && !isOpen) {
      const timer = setTimeout(() => {
        setIsTooltipClosing(true);
        // Wait for animation out duration
        setTimeout(() => {
          setShowTooltip(false);
          setIsTooltipClosing(false);
          // Set timer to re-show in 1 minute
          const reShowTimer = setTimeout(() => {
            setShowTooltip(true);
          }, 60000);
          return () => clearTimeout(reShowTimer);
        }, 400);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
      return;
    }

    if (isDesktop && !isExpanded) {
      document.body.style.overflow = '';
      return;
    }

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    if (widgetWrapperRef.current && !isDesktop) widgetWrapperRef.current.style.top = '';

    const updateMobileViewport = () => {
      if (!widgetWrapperRef.current || isDesktop) return;
      const vv = (window as any).visualViewport;

      if (vv) {
        // Only use vv.height if keyboard is likely open (height is reduced)
        // Otherwise use 100dvh to ensure full coverage on all mobile browsers
        const isKeyboardOpen = vv.height < window.innerHeight - 150;

        if (isKeyboardOpen) {
          widgetWrapperRef.current.style.height = `${vv.height}px`;
          // Important: Update top to match visual viewport offset to handle iOS scrolling/panning
          widgetWrapperRef.current.style.top = `${vv.offsetTop}px`;
        } else {
          widgetWrapperRef.current.style.height = '100dvh';
          widgetWrapperRef.current.style.top = '0px';
        }
      } else {
        widgetWrapperRef.current.style.height = '100dvh';
        widgetWrapperRef.current.style.top = '0px';
      }
    };

    // Listen to visualViewport changes
    if ((window as any).visualViewport) {
      const vv = (window as any).visualViewport;
      vv.addEventListener('resize', updateMobileViewport);
      vv.addEventListener('scroll', updateMobileViewport);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateMobileViewport);

    // Initial setup
    updateMobileViewport();

    return () => {
      document.body.style.overflow = originalStyle;
      if ((window as any).visualViewport) {
        const vv = (window as any).visualViewport;
        vv.removeEventListener('resize', updateMobileViewport);
        vv.removeEventListener('scroll', updateMobileViewport);
      }
      window.removeEventListener('resize', updateMobileViewport);
    };
  }, [isOpen, isDesktop, isExpanded]);

  // Prevent background page from scrolling when mouse wheel is used or touch gestures occur on/inside the widget
  useEffect(() => {
    const wrapper = widgetWrapperRef.current;
    if (!wrapper || !isOpen) return;

    let touchStartClientY = 0;

    const getScrollableContainer = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof HTMLElement)) return null;
      // Fast path: target is or is inside chat messages container, textarea, or scrollable element
      const container = target.closest('.vw-chat-messages-container, textarea, [data-scrollable="true"]') as HTMLElement | null;
      if (container && wrapper.contains(container) && container.scrollHeight > container.clientHeight) {
        return container;
      }
      // Fallback: traverse up to find any container with overflow-y auto/scroll
      let el: HTMLElement | null = target;
      while (el && el !== wrapper) {
        const style = window.getComputedStyle(el);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    const handleTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length === 1) {
        touchStartClientY = e.touches[0].clientY;

        // iOS Safari 1px Scroll Offset trick to prevent boundary overscroll chaining
        const scrollableContainer = getScrollableContainer(e.target);
        if (scrollableContainer) {
          const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
          if (scrollTop <= 0) {
            scrollableContainer.scrollTop = 1;
          } else if (scrollTop + clientHeight >= scrollHeight) {
            scrollableContainer.scrollTop = scrollHeight - clientHeight - 1;
          }
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const deltaY = touchStartClientY - currentY; // positive = scrolling down, negative = scrolling up

      const scrollableContainer = getScrollableContainer(e.target);

      if (scrollableContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
        const scrollingUp = deltaY < 0;
        const scrollingDown = deltaY > 0;

        if (scrollingUp && scrollTop <= 0) {
          if (e.cancelable) e.preventDefault();
        } else if (scrollingDown && scrollTop + clientHeight >= scrollHeight - 1) {
          if (e.cancelable) e.preventDefault();
        }
      } else {
        if (e.cancelable) e.preventDefault();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();

      const scrollableContainer = getScrollableContainer(e.target);

      if (scrollableContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
        const delta = e.deltaY;
        const scrollingUp = delta < 0;
        const scrollingDown = delta > 0;

        if (scrollingUp && scrollTop <= 0) {
          if (e.cancelable) e.preventDefault();
        } else if (scrollingDown && scrollTop + clientHeight >= scrollHeight - 1) {
          if (e.cancelable) e.preventDefault();
        }
      } else {
        // Prevent background scrolling entirely when wheeling on non-scrollable parts
        if (e.cancelable) e.preventDefault();
      }
    };

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('wheel', handleWheel);
    };
  }, [isDesktop, isOpen, isExpanded]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          setQuestion(prev => (prev ? prev + ' ' + transcript : transcript));
          setIsListening(false);
          // Auto-focus textarea after dictation
          if (textareaRef.current) {
            textareaRef.current.focus();
            // Adjust height
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 80)}px`;
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error', event.error);
          setIsListening(false);
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }
    }
  }, []);



  // Focus trap
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
          if (!chatContainerRef.current?.parentElement) return;
          const modal = chatContainerRef.current.parentElement; // The main wrapper
          const focusableContent = modal.querySelectorAll(focusableElements);
          if (focusableContent.length === 0) return;

          const firstElement = focusableContent[0] as HTMLElement;
          const lastElement = focusableContent[focusableContent.length - 1] as HTMLElement;

          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      // We are in Shadow DOM, so document.activeElement might behave differently if not handled carefully,
      // but event listener on window or document works for keydown.
      // However, we need to attach to render root or similar. 
      // Since we are inside the component, we can listen on the window.
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLoading && !isStreaming) {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0);
    }
  }, [isLoading, isStreaming]);

  useEffect(() => {
    if (messages.length === 0) {
      setShowPredefinedQuestions(true);
      if (qualification) setQualification(null);
      if (yourTurn) setYourTurn(null);
      if (contactForm) setContactForm(null);
      if (dynamicSuggestions.length > 0) setDynamicSuggestions([]);
    } else {
      setShowPredefinedQuestions(false);
    }
  }, [messages.length]);

  useEffect(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    if (messages.length === 0 && dynamicSuggestions.length === 0) {
      storageRef.current.clearChatHistory(sessionId);
      return;
    }

    storageRef.current.saveChatHistory(sessionId, {
      messages,
      suggestions: dynamicSuggestions,
      feedback: messageFeedback,
      // Saved with the transcript because they belong to the last reply. A
      // restored conversation ending in a question, with no buttons under it,
      // asks the visitor something they have no way to answer — and a contact
      // form that disappears on refresh loses the lead it was collecting.
      qualification,
      yourTurn,
      contactForm,
      contactValues
    });
  }, [messages, dynamicSuggestions, messageFeedback, qualification, yourTurn, contactForm, contactValues]);

  const handleAdvisorMessagesChange = useCallback((newAdvisorMsgs: any[]) => {
    const mapped: ChatMessage[] = newAdvisorMsgs
      .filter((m) => !m.streaming && m.text)
      .map((m) => ({
        type: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        text: m.text,
        timestamp: Date.now(),
        followups: m.followups && m.followups.length > 0 ? m.followups : undefined
      }));
    setMessages(mapped);

    const lastBot = [...newAdvisorMsgs].reverse().find((m) => m.role === 'bot' && !m.streaming);
    if (lastBot && lastBot.followups && lastBot.followups.length > 0) {
      setDynamicSuggestions(lastBot.followups);
    }
  }, []);



  useLayoutEffect(() => {
    if (isOpen && messages.length > 0 && chatContainerRef.current) {
      const container = chatContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length > 0 && chatContainerRef.current) {
      const container = chatContainerRef.current;
      const handle = requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
      return () => cancelAnimationFrame(handle);
    }
  }, [isOpen]);


  const loadInitialSuggestions = async () => {
    if (isLoadingSuggestions || config.suggestions?.enabled === false) return;

    setIsLoadingSuggestions(true);
    try {
      // Get current page URL path for page-specific suggestions
      const currentPageUrl = getCurrentPageUrl();
      const suggestions = await apiRef.current.fetchInitialSuggestions(
        true,
        currentPageUrl
      );

      if (suggestions.length > 0) {
        setInitialSuggestions(suggestions);
      } else if (config.suggestions?.fallbackQuestions && config.suggestions.fallbackQuestions.length > 0) {
        setInitialSuggestions(config.suggestions.fallbackQuestions);
      } else {
        setInitialSuggestions(FALLBACK_QUESTIONS);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      if (config.suggestions?.fallbackQuestions && config.suggestions.fallbackQuestions.length > 0) {
        setInitialSuggestions(config.suggestions.fallbackQuestions);
      } else {
        setInitialSuggestions(FALLBACK_QUESTIONS);
      }
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const askQuestion = async (
    questionText: string,
    queryType?: number,
    qualificationAnswer?: QualificationAnswerPayload
  ) => {
    if (isLoading || isStreaming || isProcessingRef.current) return;

    isProcessingRef.current = true;

    // Whatever they did — clicked an option, typed their own answer, or asked
    // something new — the previous turn's question is closed. Clearing both
    // here rather than per path means no route can leave a stale row behind.
    setQualification(null);
    setYourTurn(null);
    setCustomFor(null);
    // The handoff belonged to the turn that offered it. Carrying it forward
    // would leave a form sitting under an unrelated answer — and Cancel used to
    // be the only way to clear it, which no longer exists.
    setContactForm(null);
    setContactValues({});

    setQuestion('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setMessages(prev => [...prev, { type: 'user', text: questionText }]);
    setIsLoading(true);
    setIsStreaming(false);
    setDynamicSuggestions([]);
    setShowPredefinedQuestions(false);
    setHasError(false);
    setLastQuestion(questionText);
    lastSendRef.current = { queryType, answer: qualificationAnswer };
    fullAnswerRef.current = '';
    typewriter.reset();
    isResponseCompleteRef.current = false;
    completeResponseReceivedRef.current = false;

    // Scroll to bottom after adding user question, but only if near bottom
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          const container = chatContainerRef.current;
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

          if (isNearBottom) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'smooth'
            });
          }
        }
      });
    });

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentSignal = abortControllerRef.current.signal;
    let errorHandled = false;

    try {
      // Reset all tracking refs before starting
      receivedChunksRef.current = false;
      firstChunkRef.current = true;
      streamingMessageIndexRef.current = -1;

      await apiRef.current.streamQuery(
        sessionIdRef.current,
        questionText,
        {
          onResponseChunk: (content: string) => {
            // Debugging: Log incoming chunks
            // console.log('Chunk received:', content);

            // [REVERTED FIX]: The check for empty chunks is removed to ensure responses are processing.
            // If the loader disappears too early, we will re-investigate with logs.

            receivedChunksRef.current = true;
            setIsLoading(false);
            setIsStreaming(true);

            // Two destinations. `fullAnswerRef` is the authoritative text and
            // is not React state, so it is immune to animation timing; the
            // typewriter only governs presentation.
            fullAnswerRef.current += content;

            setMessages(prev => {
              if (firstChunkRef.current) {
                // First chunk - create new message and track its index
                firstChunkRef.current = false;
                streamingMessageIndexRef.current = prev.length;
                setIsActivelyStreaming(true); // Signal to render StreamingMessage
                return [...prev, { type: 'assistant', text: '' }]; // Placeholder, will be filled on completion
              }
              return prev; // The typewriter drives the visible text.
            });

            // push() appends to a ref and schedules a frame if none is
            // pending, so the first character lands one frame after the first
            // chunk parses — no timer, debounce, or minimum-buffer threshold.
            typewriter.push(content);
          },
          onCompleteResponse: (content: string) => {
            setIsLoading(false);
            // We do NOT set isStreaming(false) here. We let the typer finish first.
            isResponseCompleteRef.current = true;
            
            // IMPORTANT: Only use complete_response content if we haven't received chunks
            // This prevents duplicate content when API sends both chunks AND complete_response
            if (!receivedChunksRef.current || fullAnswerRef.current.length === 0) {
              fullAnswerRef.current = content;
              // If we haven't started streaming yet, start now
              if (firstChunkRef.current) {
                firstChunkRef.current = false;
                setMessages(prev => {
                  streamingMessageIndexRef.current = prev.length;
                  return [...prev, { type: 'assistant', text: '' }];
                });
                setIsStreaming(true);
                setIsActivelyStreaming(true);
              }
              typewriter.push(content);
            }
            // If we already have chunks, the buffer already has the correct content
            // from the accumulated chunks, so we don't overwrite it
            completeResponseReceivedRef.current = true;
          },
          onSuggestedQuestions: (questions: string[]) => {
            setDynamicSuggestions(questions);
            setMessages(prev => {
              const lastIdx = prev.length - 1;
              if (lastIdx >= 0 && prev[lastIdx].type === 'assistant') {
                const updated = [...prev];
                updated[lastIdx] = { ...updated[lastIdx], followups: questions };
                return updated;
              }
              return prev;
            });
          },
          onSources: (sources) => {
            setLoadingMessageIndex(1); // 'Searching the knowledge base...'
            setMessages(prev => {
              const lastMessage = prev[prev.length - 1];
              if (lastMessage && lastMessage.type === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  { ...lastMessage, sources }
                ];
              }
              return prev;
            });
          },
          onMetadata: (metadata) => {
            setLoadingMessageIndex(2); // 'Processing information...'
            setMessages(prev => {
              const lastMessage = prev[prev.length - 1];
              if (lastMessage && lastMessage.type === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  { ...lastMessage, metadata }
                ];
              }
              return prev;
            });
          },
          onContactForm: (contactPayload: ContactFormPayload) => {
            setContactForm(contactPayload);
            // Admin-configured prefills, applied once when the form arrives.
            const seed: Record<string, string> = {};
            for (const f of contactPayload.fields) {
              if (f.value) seed[f.field_name] = f.value;
            }
            setContactValues(seed);
          },
          onQualificationOptions: (payload: QualificationOptionsPayload) => {
            // Never arrives with suggestions — the backend suppresses those on
            // a turn that asks a question with options, so only one row of
            // choices ever sits under a reply. Also used for a question asked
            // in words with buttons off (`answer_type: 'text'`): nothing
            // renders, but holding it here keeps the fallback suggestions
            // below from filling the space the backend left empty on purpose.
            setQualification(payload);
            setDynamicSuggestions([]);
          },
          onYourTurn: (payload: QualificationOptionsPayload) => {
            // Rides with the suggestions (the backend sends two, not three).
            setYourTurn(payload);
          },
          onDone: () => {
            // CRITICAL: Mark response as complete so typing effect can finalize
            // This is needed because the API may send 'done' without 'complete_response'
            isResponseCompleteRef.current = true;
            // Does NOT snap to the end: the reveal keeps draining what is
            // queued and settles itself once the buffer empties.
            typewriter.end();
            
            // If no chunks were received at all, finalize immediately
            if (!receivedChunksRef.current && !isStreaming) {
              setIsLoading(false);
              isProcessingRef.current = false;
            }
          },
          onError: (error: string) => {
            if (currentSignal.aborted || errorHandled) return;
            errorHandled = true;
            console.error('Streaming error:', error);
            isProcessingRef.current = false;

            if (receivedChunksRef.current && fullAnswerRef.current.length > 0) {
              isResponseCompleteRef.current = true;
              typewriter.end();
              setIsLoading(false);
              return;
            }

            // Nothing to animate for a request that was abandoned.
            typewriter.flush();

            const errorMsg = config.messages?.errorMessage || "I apologize, but I'm having trouble connecting right now.";
            setMessages(prev => [...prev, { type: 'assistant', text: errorMsg }]);
            setIsLoading(false);
            setIsStreaming(false);
          }
        },
        queryType,
        getCurrentPageUrl(),
        getCampaignRef(),
        currentSignal,
        qualificationAnswer
      );
    } catch (error) {
      if (currentSignal.aborted) {
        return;
      }
      console.error('Error getting response:', error);
      isProcessingRef.current = false;
      setHasError(true);
      setIsLoading(false);
      if (!errorHandled) {
        errorHandled = true;
        if (receivedChunksRef.current && fullAnswerRef.current.length > 0) {
          isResponseCompleteRef.current = true;
          typewriter.end();
        } else {
          setIsStreaming(false);
          const errorMsg = config.messages?.errorMessage || "I apologize, but I'm having trouble connecting right now.";
          setMessages(prev => [...prev, { type: 'assistant', text: errorMsg }]);
        }
      }
    }
  };

  // Hand the message back to its own committed text once the reveal has caught
  // up. Both conditions matter: the stream has to be over AND the revealed text
  // has to actually equal the full answer. Releasing on `!isTyping` alone swaps
  // in the complete text while characters are still queued, snapping the tail
  // onto the screen instantly — which is the one artifact the paced reveal
  // exists to avoid.
  useEffect(() => {
    if (!isActivelyStreaming) return;
    if (!isResponseCompleteRef.current) return;
    if (typewriter.isTyping) return;
    if (!fullAnswerRef.current) return;

    if (typewriter.text !== fullAnswerRef.current) {
      typewriter.flush();
      return;
    }

    const finalText = fullAnswerRef.current;
    const idx = streamingMessageIndexRef.current;

    setMessages(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = [...prev];
      const existingFollowups = updated[idx].followups;
      const followups = (existingFollowups && existingFollowups.length > 0)
        ? existingFollowups
        : (dynamicSuggestions.length > 0 ? dynamicSuggestions : undefined);
      updated[idx] = { ...updated[idx], text: finalText, followups };
      return updated;
    });

    setIsActivelyStreaming(false);
    setIsStreaming(false);
    isProcessingRef.current = false;

    // If dynamicSuggestions is still empty and no qualification/contactForm was offered,
    // provide fallback follow-up suggestions so the user always has suggested questions to explore
    if (!qualification && !contactForm) {
      setDynamicSuggestions(prev => {
        if (prev && prev.length > 0) return prev;
        return initialSuggestions.length > 0 ? initialSuggestions : FALLBACK_QUESTIONS;
      });
    }

    // Auto-focus the input so the user can type immediately
    setTimeout(() => {
      if (textareaRef.current && isDesktop) {
        textareaRef.current.focus();
      }
    }, 10);
  }, [isActivelyStreaming, typewriter.isTyping, typewriter.text, isDesktop, qualification, contactForm, initialSuggestions, dynamicSuggestions]);

  // Safety fallback: if response is complete over network, guarantee flush within 2.5s
  useEffect(() => {
    if (!isActivelyStreaming || !isResponseCompleteRef.current) return;
    const safetyTimer = setTimeout(() => {
      if (isActivelyStreaming && isResponseCompleteRef.current) {
        typewriter.flush();
      }
    }, 2500);
    return () => clearTimeout(safetyTimer);
  }, [isActivelyStreaming]);

  const handleCopy = async (text: string, index: number) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopiedMessageId(index);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      // Fallback for iOS/older browsers/HTTP
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Improve iOS compatibility by ensuring editable and preventing zoom
      textArea.style.position = 'fixed'; // Avoid scrolling to bottom
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      textArea.contentEditable = "true";
      textArea.readOnly = false;

      document.body.appendChild(textArea);

      // Select text
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
      }

      try {
        document.execCommand('copy');
        setCopiedMessageId(index);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (err) {
        console.error('Copy failed', err);
      }
      document.body.removeChild(textArea);
    }
  };



  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    typewriter.flush();

    setIsStreaming(false);
    setIsLoading(false);
    isProcessingRef.current = false;

    setMessages(prev => {
      // If we were streaming, limit the last message to what was shown?
      // Actually, the typing effect updates the state. We just add a new message indicating stop.
      // Or we can append to the current text? The user asked for a "message".
      return [...prev, { type: 'assistant', text: 'You stopped this response' }];
    });

    // Show suggestions (fallback to initial/default since we interrupted the generation)
    if (dynamicSuggestions.length === 0) {
      setDynamicSuggestions(initialSuggestions.length > 0 ? initialSuggestions : FALLBACK_QUESTIONS);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    // They picked "Something else", so this typed message IS the answer to
    // that question — recorded against it rather than read as a new one. Any
    // other typed message is an ordinary query, including one typed while a
    // question sits unanswered above: ignoring the chips is always allowed.
    const pending = customFor;
    const answer: QualificationAnswerPayload | undefined = pending
      ? {
          question_id: pending.question_id,
          answer_type: pending.answer_type,
          selected: [{ value: CUSTOM_OPTION_VALUE, label: 'Something else' }],
          is_custom: true,
          custom_text: question.trim(),
        }
      : undefined;

    await askQuestion(question, answer ? QUERY_TYPE.QUALIFICATION_CUSTOM : QUERY_TYPE.TYPED, answer);
  };

  // ... (rest of code)
  // Then update the button onClick lower down


  const handleSuggestedQuestionClick = async (questionText: string) => {
    await askQuestion(questionText, QUERY_TYPE.SUGGESTED);
  };

  const handleDynamicSuggestionClick = async (suggestion: string) => {
    setClickedSuggestions(prev => new Set(prev).add(suggestion));
    await askQuestion(suggestion, QUERY_TYPE.SUGGESTED);
  };

  const handleCollapsedClick = () => {
    setIsOpen(true);
    if (config.callbacks?.onOpen) {
      config.callbacks.onOpen();
    }
  };

  const closeWidget = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (config.callbacks?.onClose) {
      config.callbacks.onClose();
    }

    if (!isDesktop) {
      // Mobile takes over the full screen already; no pop-out anchor to animate toward.
      setIsOpen(false);
      setIsExpanded(false);
      return;
    }

    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsExpanded(false);
      setIsClosing(false);
    }, 350);
  };

  const handleFeedback = async (messageIndex: number, feedbackType: 'positive' | 'negative') => {
    if (messageFeedback[messageIndex] === feedbackType) return;

    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: feedbackType
    }));

    try {
      await apiRef.current.submitChatFeedback(
        sessionIdRef.current,
        feedbackType === 'positive' ? 1 : 0
      );
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const isBottomLeft = config.behavior?.position === 'bottom-left';

  if (!isOpen) {
    return (
      <div 
        className="vw-fixed vw-z-[9999] vw-pointer-events-none vw-flex vw-items-center vw-justify-end"
        style={{ 
          bottom: 'max(45px, env(safe-area-inset-bottom))', 
          ...(isBottomLeft
            ? { left: 'max(24px, env(safe-area-inset-left))', right: 'auto' }
            : { right: 'max(24px, env(safe-area-inset-right))', left: 'auto' }
          ),
          width: 'auto',
          height: 'auto'
        }}
      >
        <div className={`vw-flex vw-items-center vw-pointer-events-auto ${isBottomLeft ? 'vw-flex-row-reverse' : ''}`}>
        {showTooltip && (
          <div 
            className={`vw-bg-white vw-rounded-lg vw-p-3 vw-relative vw-border vw-border-gray-100 ${isBottomLeft ? 'vw-ml-4' : 'vw-mr-4'} ${isTooltipClosing ? 'vw-tooltip-animate-out' : 'vw-tooltip-animate-in'}`}
            style={{ width: '220px' }}
          >
            <p className="vw-text-gray-700 vw-text-sm">How may I help you today?</p>
            {/* Tooltip Arrow — flips side based on position */}
            <div 
              className={`vw-absolute vw-top-1/2 vw-w-3 vw-h-3 vw-bg-white vw-border-gray-100 vw-rotate-45 ${isBottomLeft ? 'vw-left-[-6px] vw-border-l vw-border-b' : 'vw-right-[-6px] vw-border-r vw-border-t'}`}
              style={{ transform: 'translateY(-50%) rotate(45deg)' }}
            ></div>
          </div>
        )}
        <button
          onClick={handleCollapsedClick}
          className="hover:vw-scale-105 vw-transition-transform vw-duration-200"
          style={{
            background: primaryColor || '#6720ff',
            borderRadius: "50px",
            padding: "11px 22px",
            width: "auto",
            height: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            boxShadow: "0 8px 24px -6px rgba(103, 32, 255, 0.45)",
            border: "none",
            cursor: "pointer"
          }}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#22c55e',
                display: 'inline-block'
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#22c55e',
                animation: 'greenPulse 2s ease-out infinite'
              }}
            />
          </div>
          <span className="vw-text-white vw-font-bold vw-text-base" style={{ letterSpacing: '-0.01em' }}>
            Chat
          </span>
        </button>
      </div>
    </div>
    );
  }

  return (
    <div
      ref={widgetWrapperRef}
      className={`vw-fixed vw-z-[9999] vw-flex vw-flex-col vw-overflow-hidden vw-pointer-events-auto ${isDesktop ? (isClosing ? 'vw-widget-pop-out' : 'vw-widget-pop-in') : ''} ${currentTheme === 'dark' ? 'vw-bg-gray-800' : 'vw-bg-white'}`}
      style={{
        pointerEvents: 'auto',
        border: isExpanded ? "none" : "1px solid #c8cbd2",
        backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#ffffff',
        transition: isDesktop ? "width 0.4s cubic-bezier(0.65, 0, 0.35, 1), height 0.4s cubic-bezier(0.65, 0, 0.35, 1), top 0.4s cubic-bezier(0.65, 0, 0.35, 1), left 0.4s cubic-bezier(0.65, 0, 0.35, 1), border-radius 0.4s cubic-bezier(0.65, 0, 0.35, 1)" : "none",
        position: 'fixed',
        overscrollBehavior: 'contain',
        willChange: isDesktop ? 'width, height, top, left' : undefined,
        transformOrigin: isBottomLeft ? 'bottom left' : 'bottom right',
        ...(isDesktop ? (
          isExpanded ? {
            // Full View Page (covers entire viewport)
            width: "100vw",
            height: "100vh",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            borderRadius: "0",
            transform: "none"
          } : {
            // Collapsed State (Bottom Right or Bottom Left)
            width: "400px",
            height: "650px",
            top: "calc(100vh - 650px - max(45px, env(safe-area-inset-bottom)))",
            left: isBottomLeft
              ? "max(24px, env(safe-area-inset-left))"
              : "calc(100vw - 400px - max(24px, env(safe-area-inset-right)))",
            right: "auto",
            bottom: "auto",
            borderRadius: "20px",
            transform: "none"
          }
        ) : {
          // Mobile State: Height is handled by Ref (visualViewport), Top is pinned to 0. Body scroll locked.
          position: 'fixed',
          width: "100%",
          top: "0",
          left: "0",
          right: "0",
          bottom: "auto",
          borderRadius: "0",
          transform: "none",
          overflow: "hidden"
        }),
        zIndex: 9999,
        // Ensure max constraints don't break layout on small desktop windows
        maxWidth: isDesktop ? (isExpanded ? "100vw" : "calc(100vw - 48px)") : "100%",
        maxHeight: isDesktop ? (isExpanded ? "100vh" : "calc(100vh - 48px)") : "100dvh",
        boxShadow: isExpanded
          ? 'none'
          : '0 12px 40px rgba(0, 0, 0, 0.15)'
      }}
    >
      {isExpanded ? (
        <ExpandedAdvisorView
          onMinimize={() => setIsExpanded(false)}
          onClose={closeWidget}
          api={apiRef.current}
          sessionId={sessionIdRef.current}
          initialMessages={messages.map((m, idx) => {
            const isLastBot = idx === messages.length - 1 && m.type === 'assistant';
            const followups = (m.followups && m.followups.length > 0)
              ? m.followups
              : (isLastBot && dynamicSuggestions.length > 0 ? dynamicSuggestions : null);
            return {
              role: m.type === 'user' ? 'user' : 'bot',
              text: m.text,
              streaming: false,
              followups
            };
          })}
          onMessagesChange={handleAdvisorMessagesChange}
          context={config.context}
          companyName={config.branding?.companyName}
          initialSuggestions={initialSuggestions}
          dynamicSuggestions={dynamicSuggestions}
          onSuggestionsChange={setDynamicSuggestions}
          initialQualification={qualification}
          onQualificationChange={setQualification}
          initialYourTurn={yourTurn}
          onYourTurnChange={setYourTurn}
          initialContactForm={contactForm}
          onContactFormChange={setContactForm}
          initialContactValues={contactValues}
          onContactValuesChange={setContactValues}
        />
      ) : (
        <>
          {/* Header */}
      <div
        className={`vw-flex vw-items-center vw-justify-between vw-py-3 vw-flex-shrink-0 ${isExpanded ? 'vw-px-6' : 'vw-px-5'} ${currentTheme === 'dark' ? 'vw-bg-gray-800' : 'vw-bg-white'}`}
        style={{
          borderBottom: currentTheme === 'dark' ? '1px solid #374151' : '1px solid #f3f4f6',
        }}
      >
        <div className="vw-flex vw-items-center vw-gap-3">
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid #d1fae5',
              background: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              flexShrink: 0
            }}
          >
            <span
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                display: 'inline-block'
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                animation: 'greenPulse 2s ease-out infinite'
              }}
            />
          </div>
          <div className="vw-flex vw-flex-col">
            <span className={`vw-font-bold vw-text-[15px] vw-leading-snug ${currentTheme === 'dark' ? 'vw-text-white' : 'vw-text-gray-900'}`}>
              {config.branding?.companyName && config.branding.companyName !== 'AI Assistant' && config.branding.companyName !== 'Vinfotech AI'
                ? config.branding.companyName
                : 'Vinfotech Product Expert'}
            </span>
            <span className={`vw-text-[12px] vw-leading-tight ${currentTheme === 'dark' ? 'vw-text-gray-400' : 'vw-text-gray-500'}`}>
              {config.branding?.poweredByText && config.branding.poweredByText !== 'Powered by'
                ? config.branding.poweredByText
                : 'Usually replies instantly'}
            </span>
          </div>
        </div>
        <div className="vw-flex vw-items-center vw-gap-3">
          {/* Email-me-a-summary. Hidden until the visitor has asked a few
              things: a recap of one question tells them nothing they do not
              remember, and the backend would refuse an empty history anyway.
              Counts THEIR messages so the icon appears at the same point as
              the full-screen view's share card. */}
          {canShareSummary && (
            <button
              onClick={() => {
                setSummaryOpen((open) => !open);
                // Opening it below the transcript is no use if it is out of
                // view, so scroll to it once React has rendered the card.
                if (!summaryOpen) {
                  setTimeout(
                    () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }),
                    50
                  );
                }
              }}
              title="Email me a summary of this conversation"
              aria-label="Email me a summary of this conversation"
              className="vw-bg-transparent vw-transition-opacity hover:vw-opacity-70"
            >
              {/* Lit in the brand colour while open, so the toggle reads as
                  on — the brand colour is runtime config, so it is set
                  inline rather than through a class. */}
              <Mail
                className="vw-w-5 vw-h-5"
                style={{ color: summaryOpen ? primaryColor : '#6B7280' }}
              />
            </button>
          )}
          {isDesktop && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Minimize' : 'Expand'}
              aria-label={isExpanded ? 'Minimize' : 'Expand'}
              className="vw-bg-transparent vw-transition-opacity hover:vw-opacity-70"
            >
              {isExpanded ? (
                <Minimize2 className="vw-w-5 vw-h-5 vw-text-gray-500" />
              ) : (
                <Maximize2 className="vw-w-5 vw-h-5 vw-text-gray-500" />
              )}
            </button>
          )}
          <button
            onClick={closeWidget}
            className="vw-bg-transparent vw-transition-opacity hover:vw-opacity-70"
          >
            <X className="vw-w-5 vw-h-5 vw-text-gray-500" />
          </button>
        </div>
      </div >

      {/* Chat Container */}
      < div
        ref={chatContainerRef}
        className={`vw-flex-1 vw-flex vw-flex-col vw-overflow-y-auto vw-chat-messages-container ${isExpanded ? 'vw-px-6' : 'vw-px-5'} ${currentTheme === 'dark' ? 'vw-bg-gray-900' : 'vw-bg-white'} ${messages.length === 0 ? 'vw-pt-6 vw-pb-0' : 'vw-py-6'}`}
        style={{ overscrollBehaviorY: 'contain' }}
      >
        {
          messages.length === 0 ? (
            <div className="vw-flex vw-flex-col vw-flex-1 vw-min-h-0">
              <div className="vw-mb-8">
                <div className={`vw-text-2xl vw-font-bold vw-mb-0 ${currentTheme === 'dark' ? 'vw-text-white' : 'vw-text-gray-900'}`}>
                  Hello,
                </div>
                <div className="vw-text-2xl vw-font-bold" style={{ color: primaryColor }}>
                  {welcomeMessage}
                </div>
              </div>

              {showPredefinedQuestions && initialSuggestions.length > 0 && (
                <div className="vw-flex vw-flex-col vw-items-start vw-gap-3 vw-mt-auto vw-pb-5">
                  {initialSuggestions.slice(0, visibleSuggestionsCount).map((q, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuestionClick(q)}
                      style={{
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        color: currentTheme === 'dark' ? '#ffffff' : '#374151',
                        borderColor: currentTheme === 'dark' ? '#4b5563' : '#d1d5db',
                        animationDelay: `${index * 70}ms`,
                        animationFillMode: 'backwards',
                      }}
                      className={`vw-animate-in vw-text-left vw-rounded-[13px] vw-border vw-transition-all vw-duration-200 vw-w-fit vw-max-w-[100%] vw-break-words vw-min-h-[40px] vw-py-2 vw-flex vw-items-center ${isExpanded ? 'vw-text-[16px]' : 'vw-text-sm'} ${currentTheme === 'dark'
                        ? 'vw-bg-transparent hover:vw-bg-gray-800'
                        : 'vw-bg-transparent hover:vw-border-gray-400'
                        }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {isLoadingSuggestions && initialSuggestions.length === 0 && (
                <div className="vw-flex vw-items-center vw-justify-center vw-gap-2 vw-mt-6">
                  <div
                    className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                    style={{ animationDelay: "0ms", backgroundColor: primaryColor }}
                  ></div>
                  <div
                    className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                    style={{ animationDelay: "150ms", backgroundColor: primaryColor }}
                  ></div>
                  <div
                    className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                    style={{ animationDelay: "300ms", backgroundColor: primaryColor }}
                  ></div>
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div key={index} className="vw-mb-4">
                  <div className={`vw-flex ${message.type === 'user' ? 'vw-justify-end' : 'vw-justify-start vw-items-start'}`}>
                    <div
                      // The assistant bubble's bottom padding is dropped when
                      // options or a form follow it: that padding exists to
                      // separate one message from the next, and here the thing
                      // below is part of the same reply — the answers to the
                      // question it just asked. Keeping it pushed them clear of
                      // the question they belong to.
                      className={`vw-transition-all vw-duration-300 ${
                        message.type === 'assistant' &&
                        index === messages.length - 1 &&
                        (hasAnswerButtons(qualification) || contactForm)
                          ? 'vw-pt-3 vw-pb-0'
                          : 'vw-py-3'
                      } ${message.type === 'user'
                        ? `${isExpanded ? 'vw-max-w-[80%]' : 'vw-max-w-[85%]'} vw-w-fit vw-px-5 vw-rounded-[20px] vw-rounded-tr-[1px]`
                        : 'vw-w-full vw-px-0'
                        }`}
                      style={{
                        backgroundColor: message.type === 'user'
                          ? (currentTheme === 'dark' ? `${primaryColor}33` : `${primaryColor}1A`)
                          : 'transparent',
                        color: currentTheme === 'dark' ? '#e5e7eb' : '#111827',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.type === 'user' ? (
                        <p className={`${isExpanded ? 'vw-text-[18px]' : 'vw-text-[15px]'} vw-leading-[1.525] vw-whitespace-pre-wrap`} style={{ whiteSpace: 'pre-wrap' }}>{message.text}</p>
                      ) : (
                        <>
                          {/* If this is the actively streaming message, render StreamingMessage component */}
                          {isActivelyStreaming && index === messages.length - 1 ? (
                            <StreamingMessage
                              content={typewriter.text}
                              isExpanded={isExpanded}
                              isDark={currentTheme === 'dark'}
                              primaryColor={primaryColor}
                              apiUrl={config.apiUrl}
                            />
                          ) : (
                            /* Normal message rendering */
                            <MarkdownRenderer
                              content={
                                index === messages.length - 1 && hasAnswerButtons(qualification) && qualification?.question
                                  ? stripQuestionFromMessage(message.text, qualification.question)
                                  : message.text
                              }
                              isExpanded={isExpanded}
                              isDark={currentTheme === 'dark'}
                              primaryColor={primaryColor}
                              apiUrl={config.apiUrl}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {/* Contact handoff, attached to the reply that offered it —
                      before the copy/feedback row for the same reason as the
                      options above. The composer stays available, so ignoring
                      the form is simply typing the next message. */}
                  {message.type === 'assistant' &&
                    index === messages.length - 1 &&
                    contactForm && !isLoading && !isStreaming && (
                      <ContactFormCard
                        contact={contactForm}
                        theme={currentTheme}
                        primaryColor={primaryColor}
                        values={contactValues}
                        onChange={setContactValues}
                        onSubmit={async (values) => {
                          const result = await apiRef.current.submitContactForm({
                            session_id: sessionIdRef.current,
                            user_query: contactForm.original_query,
                            contact_data: values,
                          });
                          if (!result.success) {
                            // Thrown so the card stays a form rather than
                            // turning into a receipt for a lead never saved.
                            throw new Error(result.error || 'submit failed');
                          }
                        }}
                      />
                    )}

                  {/* The discovery question's answers, attached to the reply
                      that asked it. Inside the message block and BEFORE the
                      copy/feedback row, because the reply ends by asking —
                      buttons sitting below a thumbs-up separate the question
                      from the answers to it.

                      Only on the newest assistant message: an older reply's
                      options are stale, the question has moved on, and
                      answering it now would confuse the transcript. */}
                  {message.type === 'assistant' &&
                    index === messages.length - 1 &&
                    hasAnswerButtons(qualification) && qualification && !isLoading && !isStreaming && (
                      <QualificationOptions
                        data={qualification}
                        theme={currentTheme}
                        primaryColor={primaryColor}
                        disabled={isLoading || isStreaming}
                        expanded={isExpanded}
                        onAnswer={(text, payload) => askQuestion(text, QUERY_TYPE.QUALIFICATION_OPTION, payload)}
                        onChooseCustom={(data) => {
                          // Hand typing back to the composer rather than
                          // opening a second input: one text box, always in the
                          // same place. The row stays visible so they can still
                          // pick an option after all.
                          //
                          // `null` releases it — they clicked "Something else"
                          // a second time, or picked a real option instead — so
                          // the composer goes back to ordinary queries and the
                          // draft they had started is dropped rather than being
                          // sent as an answer they no longer mean.
                          setCustomFor(data);
                          if (data) textareaRef.current?.focus();
                          else setQuestion('');
                        }}
                        onSkip={() => {
                          setQualification(null);
                          setCustomFor(null);
                        }}
                      />
                    )}

                  {/* Feedback buttons for assistant messages */}
                  {message.type === 'assistant' && (
                    <div className="vw-flex vw-items-center vw-gap-3 vw-mt-2">
                      <button
                        onClick={() => handleCopy(message.text, index)}
                        className="vw-bg-transparent vw-transition-opacity hover:vw-opacity-70 vw-flex vw-items-center vw-gap-1"
                      >
                        {copiedMessageId === index ? (
                          <>
                            <div className="vw-text-[10px] vw-bg-gray-800 vw-text-white vw-px-1.5 vw-py-0.5 vw-rounded">Copied</div>
                            <Copy 
                              className="vw-w-[18px] vw-h-[18px] vw-stroke-[1.5]" 
                              style={{ color: primaryColor }} 
                            />
                          </>
                        ) : (
                          <Copy className="vw-w-[18px] vw-h-[18px] vw-text-gray-400 vw-stroke-[1.5]" />
                        )}
                      </button>
                      <button
                        onClick={() => handleFeedback(index, 'positive')}
                        className="vw-bg-transparent vw-transition-opacity hover:vw-opacity-70"
                        style={{ 
                          color: messageFeedback[index] === 'positive' ? primaryColor : '#9ca3af' 
                        }}
                      >
                        <ThumbsUp className="vw-w-[18px] vw-h-[18px] vw-stroke-[1.5]" />
                      </button>
                      <button
                        onClick={() => handleFeedback(index, 'negative')}
                        className={`vw-bg-transparent vw-transition-opacity hover:vw-opacity-70 ${messageFeedback[index] === 'negative'
                          ? 'vw-text-red-600'
                          : 'vw-text-gray-400'
                          }`}
                      >
                        <ThumbsDown className="vw-w-[18px] vw-h-[18px] vw-stroke-[1.5]" />
                      </button>

                    </div>
                  )}
                </div>
              ))}

              {isLoading && !isStreaming && (
                <div className="vw-flex vw-flex-col vw-gap-3">
                  {/* Bot typing indicator row */}
                  <div className="vw-flex vw-items-start vw-gap-2">
                    <div className="vw-inline-flex vw-items-center vw-gap-2">
                      <div className="vw-flex vw-gap-1">
                        <div
                          className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                          style={{ animationDelay: "0ms", backgroundColor: primaryColor }}
                        />
                        <div
                          className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                          style={{ animationDelay: "150ms", backgroundColor: primaryColor }}
                        />
                        <div
                          className="vw-w-2 vw-h-2 vw-rounded-full vw-animate-bounce"
                          style={{ animationDelay: "300ms", backgroundColor: primaryColor }}
                        />
                      </div>
                      <span className={`vw-text-sm ${currentTheme === 'dark' ? 'vw-text-gray-400' : 'vw-text-gray-600'}`}>
                        {loadingMessages[loadingMessageIndex]}
                      </span>
                    </div>
                  </div>
                  {/* Loading skeleton bars */}
                  <div className="vw-flex vw-flex-col vw-gap-2">
                    <div className={`vw-h-3 vw-rounded-full vw-w-full vw-animate-pulse ${currentTheme === 'dark' ? 'vw-bg-gray-700' : 'vw-bg-gray-200'}`} />
                    <div
                      className={`vw-h-3 vw-rounded-full vw-w-full vw-animate-pulse ${currentTheme === 'dark' ? 'vw-bg-gray-700' : 'vw-bg-gray-200'}`}
                      style={{ animationDelay: "75ms" }}
                    />
                    <div
                      className={`vw-h-3 vw-rounded-full vw-w-3/4 vw-animate-pulse ${currentTheme === 'dark' ? 'vw-bg-gray-700' : 'vw-bg-gray-200'}`}
                      style={{ animationDelay: "150ms" }}
                    />
                  </div>
                </div>
              )}

              {/* Dynamic suggestions, and the Your Turn chip as the last one */}
              {(dynamicSuggestions.length > 0 || yourTurn) && !isLoading && !isStreaming && (
                <div className="vw-mt-4 vw-w-full">
                  <p className={`${currentTheme === 'dark' ? 'vw-text-gray-400' : 'vw-text-gray-500'} vw-mb-4 ${isExpanded ? 'vw-text-[16px]' : 'vw-text-sm'}`}>You can also ask</p>

                  <div className="vw-flex vw-flex-col vw-items-start vw-justify-start vw-gap-4">
                    {(() => {
                      const unclicked = dynamicSuggestions.filter((s) => !clickedSuggestions.has(s));
                      const toRender = unclicked.length > 0 ? unclicked : dynamicSuggestions;
                      // Two suggestions and the Your Turn chip, never four rows.
                      return toRender.slice(0, yourTurn ? 2 : 3).map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleDynamicSuggestionClick(suggestion)}
                          style={{ animationDelay: `${idx * 70}ms`, animationFillMode: 'backwards' }}
                          className="vw-animate-in vw-text-left hover:vw-opacity-70 vw-transition-opacity vw-duration-200 vw-group vw-w-fit vw-bg-transparent vw-p-0"
                        >
                          <p
                            className="vw-mb-1 vw-leading-tight"
                            style={{
                              color: currentTheme === 'dark' ? '#ffffff' : '#333333',
                              fontSize: isExpanded ? "16px" : "13px",
                              fontWeight: 400,
                            }}
                          >
                            {suggestion}
                          </p>
                          <div className={`vw-border-b vw-border-dashed ${currentTheme === 'dark' ? 'vw-border-gray-600' : 'vw-border-gray-300'} vw-w-full`} />
                        </button>
                      ));
                    })()}
                    {yourTurn && (
                      <YourTurnChip
                        key={yourTurn.question_id}
                        data={yourTurn}
                        theme={currentTheme}
                        primaryColor={primaryColor}
                        disabled={isLoading || isStreaming}
                        expanded={isExpanded}
                        onAnswer={(text, payload) => askQuestion(text, QUERY_TYPE.QUALIFICATION_OPTION, payload)}
                        onChooseCustom={(data) => {
                          // Same as the options under a reply: "Something else"
                          // points the composer at this question.
                          setCustomFor(data);
                          if (data) textareaRef.current?.focus();
                          else setQuestion('');
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* "Email me a summary", opened from the header. Sits at the end
                  of the transcript rather than over the composer, so the
                  visitor can keep chatting while it is open — and the recap
                  they get then includes whatever they ask next. */}
              {summaryOpen && canShareSummary && (
                <ChatSummaryCard
                  theme={currentTheme}
                  primaryColor={primaryColor}
                  // Prefilled from the contact form when they already typed it
                  // there, so the same details are not asked for twice.
                  defaultName={contactValues.name || contactValues.full_name}
                  defaultEmail={contactValues.email}
                  onSubmit={async (values) =>
                    apiRef.current.requestChatSummary({
                      session_id: sessionIdRef.current,
                      name: values.name,
                      email: values.email,
                      campaign_ref: getCampaignRef(),
                    })
                  }
                />
              )}

              <div ref={messagesEndRef} />
            </>
          )
        }

        {/* Error Retry State */}
        {
          hasError && !isLoading && (
            <div className="vw-flex vw-justify-center vw-mb-4">
              <button
                onClick={() => askQuestion(lastQuestion, lastSendRef.current.queryType, lastSendRef.current.answer)}
                className={`vw-flex vw-items-center vw-gap-2 vw-px-4 vw-py-2 vw-border vw-border-red-200 vw-rounded-full vw-shadow-sm vw-transition-colors vw-text-red-500 vw-text-sm ${currentTheme === 'dark' ? 'vw-bg-gray-800 hover:vw-bg-gray-700' : 'vw-bg-white hover:vw-bg-gray-50'}`}
              >
                <RefreshCcw className="vw-w-4 vw-h-4" />
                Retry
              </button>
            </div>
          )
        }
      </div >

      {/* Offline Banner */}
      {
        !isOnline && (
          <div className="vw-bg-red-500 vw-text-white vw-text-xs vw-py-1 vw-px-4 vw-text-center">
            You are offline. Please checks your internet connection.
          </div>
        )
      }

      {/* Input Area */}
      <div
        className={`vw-flex-shrink-0 vw-chat-input-footer ${messages.length === 0 ? 'vw-pt-3 vw-pb-3' : 'vw-py-3'} ${isExpanded ? 'vw-px-6' : 'vw-px-5'} ${currentTheme === 'dark' ? 'vw-bg-gray-800' : 'vw-bg-white'}`}
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
          <form onSubmit={handleSubmit} className="vw-flex vw-items-center vw-gap-3">
            <div
              className={`vw-flex-1 vw-flex vw-items-center vw-rounded-[18px] vw-border vw-transition-all vw-duration-200 ${currentTheme === 'dark' ? 'vw-bg-gray-800' : 'vw-bg-white'} ${currentTheme === 'dark' ? 'vw-border-gray-600' : 'vw-border-gray-300'}`}
              style={{
                borderColor: (question.trim() || isFocused) ? primaryColor : undefined,
                boxShadow: (question.trim() || isFocused) 
                  ? `0 0 0 1px ${primaryColor}` 
                  : undefined
              }}
            >
              <textarea
                ref={textareaRef}
                value={question}
                onChange={(e) => {
                  setQuestion(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 80)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && isDesktop) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={
                  customFor
                    ? 'Type your own answer…'
                    : isTypedQuestion(qualification) ? 'Type your answer…' : placeholderText
                }
                disabled={isLoading || isStreaming}
                rows={1}
                className={`vw-flex-1 vw-resize-none vw-rounded-[18px] vw-px-[15px] vw-py-4 vw-h-full vw-outline-none vw-bg-transparent ${currentTheme === 'dark' ? 'vw-text-gray-100 placeholder:vw-text-gray-400' : 'vw-text-gray-900 placeholder:vw-text-gray-500'} ${isExpanded ? 'vw-text-[18px]' : 'vw-text-base'}`}
                style={{ maxHeight: "80px", minHeight: "56px", overscrollBehaviorY: 'none' }}
              />

              {/* Input Actions: Mic & Send */}
              <div className="vw-flex vw-items-center vw-gap-1 vw-mr-2">
                {/* Microphone Button */}
                <button
                  type="button"
                  onClick={toggleListening}
                  disabled={isLoading || isStreaming}
                  className={`vw-p-2 vw-rounded-full vw-transition-all vw-duration-200 disabled:vw-opacity-40 disabled:vw-cursor-not-allowed ${isListening
                    ? 'vw-bg-red-100 vw-text-red-600'
                    : `vw-bg-transparent ${currentTheme === 'dark' ? 'hover:vw-bg-gray-700 vw-text-gray-300' : 'hover:vw-bg-gray-100 vw-text-gray-500'}`
                    }`}
                  title="Voice input"
                >
                  {isListening ? (
                    <MicOff className="vw-w-5 vw-h-5" />
                  ) : (
                    <Mic className="vw-w-5 vw-h-5" />
                  )}
                </button>

                <button
                  type="submit"
                  disabled={!question.trim() && !isLoading && !isStreaming}
                  onClick={(e) => {
                    if (isStreaming || isLoading) {
                      e.preventDefault();
                      handleStop();
                    }
                  }}
                  className={`vw-p-3 vw-rounded-full vw-transition-all vw-duration-200 disabled:vw-cursor-not-allowed ${!(question.trim() || isStreaming || isLoading)
                    ? `${currentTheme === 'dark' ? 'vw-bg-gray-700 hover:vw-bg-gray-600' : 'vw-bg-gray-200 hover:vw-bg-gray-300'}`
                    : 'hover:vw-opacity-90'
                    }`}
                  style={{
                    backgroundColor: (question.trim() || isStreaming || isLoading) ? primaryColor : undefined
                  }}
                >
                  {isStreaming || isLoading ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="5" width="10" height="10" rx="1" fill="#FFFFFF" />
                    </svg>
                  ) : question.trim() ? (
                    <svg width="21" height="19" viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill="#FFFFFF"
                        d="M21 9.5C21 8.3803797 20 8 20 8L0 0L3.6035199 8.5605497L13 9.5L3.6035199 10.43945L0 19L20 11C20 11 21 10.61962 21 9.5Z"
                      />
                    </svg>
                  ) : (
                    <svg width="21" height="19" viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill="#909090"
                        d="M21 9.5C21 8.3803797 20 8 20 8L0 0L3.6035199 8.5605497L13 9.5L3.6035199 10.43945L0 19L20 11C20 11 21 10.61962 21 9.5Z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {!isOnline && (
              <div className="vw-absolute vw-right-20 vw-top-1/2 -vw-translate-y-1/2">
                <WifiOff className="vw-w-5 vw-h-5 vw-text-gray-400" />
              </div>
            )}
          </form>
        </div>
      </>
    )}
  </div>
);
}
